<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M7 (7.4).
 * Account-level Eva spend card (D1): the 30-day token total + a per-model breakdown (the M5 UsageEvent
 * log, finally surfaced), and a token spend cap an admin can set (0 = no cap). Once spend reaches the
 * cap the runner stops replying. Account-wide (all assistants), so the page renders it after the
 * per-assistant editor. Tailwind-only; Composition API; self-contained for vitest.
 */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaUsageAPI from 'dashboard/api/eva/usage';
import EvaAccountSettingsAPI from 'dashboard/api/eva/accountSettings';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const total = ref(0);
const byModel = ref([]);
const cap = ref(0);
const isSaving = ref(false);
const status = ref(''); // '' | 'saved' | 'error'

const load = async () => {
  try {
    const { data } = await EvaUsageAPI.get();
    total.value = data.total.total;
    byModel.value = data.by_model;
    cap.value = data.cap;
  } catch {
    status.value = 'error';
  }
};

const saveCap = async () => {
  isSaving.value = true;
  status.value = '';
  try {
    await EvaAccountSettingsAPI.update({
      eva_spend_cap: Number(cap.value) || 0,
    });
    status.value = 'saved';
  } catch {
    status.value = 'error';
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);

defineExpose({ saveCap });
</script>

<template>
  <section
    class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
  >
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('EVA_USAGE.TITLE') }}
    </h3>

    <p class="flex items-baseline gap-2">
      <span class="text-2xl font-semibold tabular-nums text-n-slate-12">
        {{ total.toLocaleString() }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ t('EVA_USAGE.TOTAL_LABEL') }}
      </span>
    </p>

    <ul
      v-if="byModel.length"
      class="flex flex-col max-w-md divide-y divide-n-weak"
    >
      <li
        v-for="row in byModel"
        :key="row.model"
        class="flex justify-between gap-4 py-1.5 text-xs text-n-slate-11"
      >
        <span class="truncate">{{ row.model }}</span>
        <span class="tabular-nums shrink-0">{{
          row.total.toLocaleString()
        }}</span>
      </li>
    </ul>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-medium text-n-slate-12" for="eva-spend-cap">
        {{ t('EVA_USAGE.CAP') }}
      </label>
      <div class="flex items-center gap-2">
        <Input
          id="eva-spend-cap"
          v-model="cap"
          type="number"
          min="0"
          class="w-40"
        />
        <Button
          :label="t('EVA_USAGE.SAVE_CAP')"
          sm
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="saveCap"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('EVA_USAGE.CAP_HINT') }}
      </p>
    </div>

    <p v-if="status === 'saved'" class="text-sm text-n-teal-11">
      {{ t('EVA_USAGE.SAVED') }}
    </p>
    <p v-else-if="status === 'error'" class="text-sm text-n-ruby-11">
      {{ t('EVA_USAGE.ERROR') }}
    </p>
  </section>
</template>
